import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './App.css';

function App() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [showColdStartWarning, setShowColdStartWarning] = useState(false);
  const [messages, setMessages] = useState([]); // Store all conversation messages

  useEffect(() => {
    // Check if user has visited before
    const hasVisited = localStorage.getItem('hasVisitedYogaApp');
    if (!hasVisited) {
      setShowColdStartWarning(true);
      localStorage.setItem('hasVisitedYogaApp', 'true');
    }
  }, []);



  const handleSubmit = async (q = query) => {
    if (!q.trim()) return;

    // Add user message to history immediately
    const userMessage = { type: 'user', content: q, timestamp: Date.now() };
    setMessages(prev => [...prev, userMessage]);

    setLoading(true);
    setError(null);
    setResponse(null);

    // Use environment variable for API URL or fall back to relative path (proxy)
    const apiUrl = process.env.REACT_APP_API_URL || '';

    try {
      const result = await axios.post(`${apiUrl}/api/ask`, { query: q });
      
      // Add assistant response to history
      const assistantMessage = {
        type: 'assistant',
        content: result.data.answer,
        data: result.data,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      setResponse(result.data);
      setQuery(''); // Clear input after submission
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to get answer');
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (helpful, messageIndex) => {
    const message = messages[messageIndex];
    const queryId = message?.data?.queryId;
    
    if (!queryId) {
      alert('Error: Query ID not found. Please try asking a new question.');
      console.error('No queryId in message:', message);
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL || '';
    try {
      const result = await axios.post(`${apiUrl}/api/feedback`, {
        queryId: queryId,
        helpful
      });
      alert('Thank you for your feedback!');
      console.log('Feedback submitted successfully:', result.data);
    } catch (err) {
      console.error('Feedback error:', err);
      console.error('Error details:', err.response?.data || err.message);
      alert(`Failed to submit feedback: ${err.response?.data?.error || err.message}`);
    }
  };

  return (
    <div className="App">
      {/* Cold Start Warning Popup */}
      {showColdStartWarning && (
        <div className="modal-overlay" onClick={() => setShowColdStartWarning(false)}>
          <div className="cold-start-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <span className="popup-icon">⚠️</span>
              <h3>First-Time Visitor Notice</h3>
            </div>
            <div className="popup-body">
              <p>
                <strong>⏱️ Please Note:</strong> The first response may take <strong>1-2 minutes</strong> as the server wakes up from sleep.
              </p>
              <p className="popup-detail">
                This application is deployed on Render's free tier, which automatically sleeps after periods of inactivity. Subsequent responses will be fast (1-2 seconds).
              </p>
            </div>
            <div className="popup-footer">
              <button 
                className="popup-button"
                onClick={() => setShowColdStartWarning(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Messages Area */}
      <div className="messages-container">
        {/* Display all messages */}
        {messages.map((message, index) => (
          <div key={index}>
            {message.type === 'user' ? (
              <div className="user-message">
                <div className="user-message-content">{message.content}</div>
              </div>
            ) : (
              <div className="message-group">
                {/* Safety Warning */}
                {message.data?.isUnsafe && (
                  <div className="safety-warning">
                    <div className="warning-header">⚠️ SAFETY NOTICE</div>
                    <div className="warning-content">
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Normal Answer */}
                {!message.data?.isUnsafe && (
                  <div className="answer-message">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                    <div className="response-footer">
                      <span className="response-time">{message.data?.responseTime}ms</span>
                    </div>
                  </div>
                )}

                {/* Sources */}
                {message.data?.sources && message.data.sources.length > 0 && (
                  <div className="sources-section">
                    <div className="sources-header">📚 Sources</div>
                    {message.data.sources.map((src, i) => (
                      <div key={i} className="source-item">
                        <div className="source-title">{src.title}</div>
                        <div className="source-meta">
                          {src.source.split('-')[0].trim()} • Page {src.page} • {(src.score * 100).toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Feedback */}
                {!message.data?.isUnsafe && (
                  <div className="feedback-row">
                    <button onClick={() => handleFeedback(true, index)}>👍</button>
                    <button onClick={() => handleFeedback(false, index)}>👎</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Response - keep for backwards compatibility during transition */}
        {response && messages.length === 0 && (
          <div className="message-group">
            {/* Safety Warning */}
            {response.isUnsafe && (
              <div className="safety-warning">
                <div className="warning-header">⚠️ SAFETY NOTICE</div>
                <div className="warning-content">
                  <ReactMarkdown>{response.answer}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Normal Answer */}
            {!response.isUnsafe && (
              <div className="answer-message">
                <ReactMarkdown>{response.answer}</ReactMarkdown>
                <div className="response-footer">
                  <span className="response-time">{response.responseTime}ms</span>
                </div>
              </div>
            )}

            {/* Sources */}
            {response.sources && response.sources.length > 0 && (
              <div className="sources-section">
                <div className="sources-header">📚 Sources</div>
                {response.sources.map((src, i) => (
                  <div key={i} className="source-item">
                    <div className="source-title">{src.title}</div>
                    <div className="source-meta">
                      {src.source.split('-')[0].trim()} • Page {src.page} • {(src.score * 100).toFixed(0)}%
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Feedback */}
            {!response.isUnsafe && (
              <div className="feedback-row">
                <button onClick={() => handleFeedback(true, messages.length - 1)}>👍</button>
                <button onClick={() => handleFeedback(false, messages.length - 1)}>👎</button>
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="loading-message">
            <div className="spinner"></div>
            <span>Searching knowledge base...</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Input Area (Fixed at Bottom) */}
      <div className="input-container">
        <div className="input-wrapper">
          <input
            type="text"
            className="query-input"
            placeholder="Ask about yoga..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            disabled={loading}
          />
          <button
            className="send-button"
            onClick={() => handleSubmit()}
            disabled={loading || !query.trim()}
          >
            ➤
          </button>
        </div>
        <div className="disclaimer">
          ⚕️ Not medical advice • Based on Common Yoga Protocol
        </div>
      </div>
    </div>
  );
}

export default App;
